import { toast } from 'sonner'

/**
 * Extracção e apresentação coerente de erros da API — STRADA.
 *
 * O backend devolve sempre { codigo, mensagem } (ver backend/src/common/errors).
 * Estas funções são o ÚNICO sítio onde se interpreta um erro de API no frontend.
 * Não reimplementar `err.response?.data?.message` espalhado pelos componentes.
 */

export interface ApiError {
  /** Código do catálogo (ex.: 'E4001'), ou null se indeterminado. */
  codigo: string | null
  /** Mensagem humana pronta a mostrar ao utilizador. */
  mensagem: string
}

/** Extrai { codigo, mensagem } de qualquer erro (axios, rede, genérico). */
export function getApiErrorMessage(err: unknown, fallback = 'Ocorreu um erro inesperado.'): ApiError {
  const e = err as {
    response?: { data?: { codigo?: string; mensagem?: string; message?: string | string[] } }
    code?: string
    message?: string
  }

  // Sem resposta do servidor → problema de rede / servidor inacessível
  if (e && 'response' in e && !e.response) {
    return { codigo: 'E0000', mensagem: 'Sem ligação ao servidor. Verifique a sua Internet.' }
  }

  const data = e?.response?.data
  if (data) {
    const codigo = data.codigo ?? null
    const raw = data.mensagem ?? data.message
    const mensagem = Array.isArray(raw) ? raw.join('; ') : raw
    if (mensagem) return { codigo, mensagem }
  }

  return { codigo: null, mensagem: e?.message || fallback }
}

/**
 * Mostra um toast de erro coerente. O código aparece de forma discreta como
 * subtítulo — o utilizador pode comunicá-lo ao suporte.
 *
 *   try { ... } catch (err) { toastApiError(err) }
 *   onError: (err) => toastApiError(err, 'Não foi possível guardar.')
 */
export function toastApiError(err: unknown, fallback?: string): ApiError {
  const { codigo, mensagem } = getApiErrorMessage(err, fallback)
  toast.error(mensagem, codigo ? { description: `Código ${codigo}` } : undefined)
  return { codigo, mensagem }
}
